import { ProcessingStage, VoiceInteractionResult } from '../models';

interface PendingRequest<T> {
  resolve: (result: T) => void;
  reject: (error: Error) => void;
  onProgress?: (stage: ProcessingStage) => void;
}

// Singleton connection state
const globalConnection: {
  ws: WebSocket | null;
  isConnected: boolean;
  isConnecting: boolean;
  listeners: Set<(connected: boolean) => void>;
} = {
  ws: null,
  isConnected: false,
  isConnecting: false,
  listeners: new Set()
};

export class WebSocketService {
  private wsUrl: string;
  private pendingRequests: Map<string, PendingRequest<any>>;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private shouldReconnect = true;
  private maxReconnectAttempts = 5;

  constructor(wsUrl: string) {
    this.wsUrl = wsUrl;
    this.pendingRequests = new Map();
  }

  public addConnectionListener(listener: (connected: boolean) => void): () => void {
    globalConnection.listeners.add(listener);
    return () => globalConnection.listeners.delete(listener);
  }

  public get isConnected(): boolean {
    return globalConnection.isConnected;
  }

  public get isConnecting(): boolean {
    return globalConnection.isConnecting;
  }

  public getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  public async connect(): Promise<void> {
    if (globalConnection.isConnecting || globalConnection.isConnected) {
      return;
    }

    try {
      globalConnection.isConnecting = true;
      this.notifyListeners();
      
      const ws = new WebSocket(this.wsUrl);
      
      return new Promise((resolve, reject) => {
        const connectTimeout = setTimeout(() => {
          ws.close();
          reject(new Error('Connection timeout'));
        }, 10000);

        ws.onopen = () => {
          clearTimeout(connectTimeout);
          globalConnection.ws = ws;
          globalConnection.isConnected = true;
          globalConnection.isConnecting = false;
          this.reconnectAttempts = 0;
          this.notifyListeners();
          resolve();
        };

        ws.onmessage = this.handleMessage.bind(this);

        ws.onerror = (error) => {
          clearTimeout(connectTimeout);
          console.error('WebSocket connection error:', error);
          globalConnection.isConnecting = false;
          this.notifyListeners();
          reject(new Error('WebSocket connection failed'));
        };

        ws.onclose = (event) => {
          clearTimeout(connectTimeout);
          
          const wasConnected = globalConnection.isConnected;
          globalConnection.isConnected = false;
          globalConnection.isConnecting = false;
          
          if (globalConnection.ws === ws) {
            globalConnection.ws = null;
          }
          
          this.notifyListeners();

          // Auto-reconnect logic
          if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts && this.shouldReconnect) {
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
            
            this.reconnectTimeout = setTimeout(() => {
              this.reconnectAttempts++;
              this.connect();
            }, delay);
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached');
            this.shouldReconnect = false;
          }
        };
      });
    } catch (error) {
      globalConnection.isConnecting = false;
      this.notifyListeners();
      console.error('Failed to create WebSocket connection:', error);
      throw error;
    }
  }

  public disconnect(): void {
    this.shouldReconnect = false;
    this.cleanup();
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const response = JSON.parse(event.data);
      
      if (response.requestId && this.pendingRequests.has(response.requestId)) {
        const request = this.pendingRequests.get(response.requestId)!;
        
        if (response.type === 'progress' && request.onProgress) {
          request.onProgress(response.stage);
        } else if (response.type === 'result') {
          // Only resolve for the final result when all data is present
          if (response.stage === 'generating' && response.data) {
            this.pendingRequests.delete(response.requestId);
            request.resolve(response.data);
          }
        } else if (response.type === 'error') {
          console.error('WebSocket request failed:', response.error);
          this.pendingRequests.delete(response.requestId);
          request.reject(new Error(response.error || 'Processing failed'));
        }
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  private cleanup(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (globalConnection.ws) {
      globalConnection.ws.close();
      globalConnection.ws = null;
    }
    
    // Reject all pending requests
    const pendingCount = this.pendingRequests.size;
    if (pendingCount > 0) {
      this.pendingRequests.forEach(({ reject }) => {
        reject(new Error('WebSocket connection lost'));
      });
      this.pendingRequests.clear();
    }
    
    globalConnection.isConnected = false;
    globalConnection.isConnecting = false;
    this.notifyListeners();
  }

  private notifyListeners(): void {
    globalConnection.listeners.forEach(listener => 
      listener(globalConnection.isConnected)
    );
  }

  /**
   * Send a message through the WebSocket connection and wait for a response
   * @param action The action type
   * @param data The data to send
   * @param onProgress Optional callback for progress updates
   * @param timeout Timeout in milliseconds (default: 60000)
   * @returns Promise resolving to the response data
   */
  public async sendMessage<T, D = any>(
    action: string,
    data: D,
    onProgress?: (stage: ProcessingStage) => void,
    timeout: number = 60000
  ): Promise<T> {
    if (!globalConnection.ws || !globalConnection.isConnected) {
      const error = new Error('WebSocket is not connected');
      console.error(`${action} failed - WebSocket not connected`);
      throw error;
    }

    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        console.error(`${action} request timeout`);
        reject(new Error('Request timeout'));
      }, timeout);

      this.pendingRequests.set(requestId, {
        resolve: (result) => {
          clearTimeout(timeoutId);
          resolve(result as T);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          console.error(`${action} failed:`, error.message);
          reject(error);
        },
        onProgress
      });

      const message = {
        action,
        requestId,
        data
      };

      try {
        const messageStr = JSON.stringify(message);
        globalConnection.ws!.send(messageStr);
      } catch (error) {
        clearTimeout(timeoutId);
        this.pendingRequests.delete(requestId);
        console.error(`Failed to send ${action} request:`, error);
        reject(new Error('Failed to send request'));
      }
    });
  }
}

export default WebSocketService; 